import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, contactsAPI } from '../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, QrCode } from 'lucide-react';
import QRCodeModal from './QRCodeModal';
import './NavigationBar.css';

const NavigationBar = () => {
    const { user } = useAuth();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [userDetails, setUserDetails] = useState(null);
    const [mobileNavBadgeCount, setMobileNavBadgeCount] = useState(0);
    const [qrModalOpen, setQrModalOpen] = useState(false);
    const [qrCodeLink, setQrCodeLink] = useState(null);
    const [loadingQRCode, setLoadingQRCode] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Listen for badge count updates from EventNavigation
    useEffect(() => {
        const handleBadgeUpdate = (event) => {
            setMobileNavBadgeCount(event.detail?.count || 0);
        };
        window.addEventListener('mobileNavBadgeUpdate', handleBadgeUpdate);
        return () => window.removeEventListener('mobileNavBadgeUpdate', handleBadgeUpdate);
    }, []);

    // Toggle mobile navigation menu
    const toggleMobileNav = () => {
        window.dispatchEvent(new CustomEvent('toggleMobileNav'));
    };

    useEffect(() => {
        // Fetch additional user details if needed
        const fetchUserDetails = async () => {
            try {
                const response = await authAPI.getCurrentUser();
                setUserDetails(response.data);
            } catch (error) {
                console.error('Error fetching user details:', error);
                // Fall back to user from context
                setUserDetails(user);
            }
        };

        if (user && !userDetails) {
            // Only fetch if we don't already have user details
            fetchUserDetails();
        }

        // Listen for profile image updates
        const handleProfileUpdate = () => {
            fetchUserDetails();
        };

        window.addEventListener('profileImageUpdated', handleProfileUpdate);
        return () => window.removeEventListener('profileImageUpdated', handleProfileUpdate);
    }, [user, userDetails]);

    useEffect(() => {
        // Close dropdown when clicking outside
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const handleLogout = () => {
        // Navigate to the logout page which clears cache, localStorage, cookies
        navigate('/logout');
    };

    const handleLogoClick = () => {
        navigate('/event/F4C2025/home');
    };

    // Get initials from first and last name
    const getInitials = () => {
        if (!userDetails) return '?';
        const firstName = userDetails.first_name || '';
        const lastName = userDetails.last_name || '';
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';
    };

    const getDisplayName = () => {
        if (!userDetails) return 'User';
        return userDetails.first_name || userDetails.email || 'User';
    };

    const fetchQRCodeLink = async () => {
        if (qrCodeLink) return qrCodeLink; // Return cached link
        
        setLoadingQRCode(true);
        try {
            const response = await contactsAPI.getMyQRCodeLink();
            if (response.data?.success && response.data?.qr_code_link) {
                setQrCodeLink(response.data.qr_code_link);
                return response.data.qr_code_link;
            } else {
                // Fallback to registration page URL if QR code link not found
                const eventMatch = location.pathname.match(/\/event\/([^/]+)/);
                if (eventMatch) {
                    const eventNameId = eventMatch[1];
                    return `${window.location.origin}/event/${eventNameId}/registration/select`;
                }
                return window.location.href;
            }
        } catch (error) {
            console.error('Error fetching QR code link:', error);
            // Fallback to registration page URL on error
            const eventMatch = location.pathname.match(/\/event\/([^/]+)/);
            if (eventMatch) {
                const eventNameId = eventMatch[1];
                return `${window.location.origin}/event/${eventNameId}/registration/select`;
            }
            return window.location.href;
        } finally {
            setLoadingQRCode(false);
        }
    };

    const handleQRCodeClick = async () => {
        setIsDropdownOpen(false);
        const link = await fetchQRCodeLink();
        setQrCodeLink(link);
        setQrModalOpen(true);
    };

    return (
        <>
            {/* QR Code Modal */}
            <QRCodeModal
                isOpen={qrModalOpen}
                onClose={() => setQrModalOpen(false)}
                url={qrCodeLink || window.location.href}
                title="Event Ticket"
            />

            <nav className="navigation-bar">
                <div className="nav-container">
                {/* Left - Hamburger (mobile) + Logo */}
                <div className="nav-left">
                    <button
                        className="mobile-hamburger-btn"
                        onClick={toggleMobileNav}
                        aria-label="Open menu"
                    >
                        <Menu size={24} />
                        {mobileNavBadgeCount > 0 && (
                            <span className="mobile-hamburger-badge">
                                {mobileNavBadgeCount > 99 ? '99+' : mobileNavBadgeCount}
                            </span>
                        )}
                    </button>
                    <img
                        src="/Funds4Charity.png"
                        alt="Funds4Charity"
                        className="nav-logo"
                        onClick={handleLogoClick}
                    />
                </div>

                {/* Center/Right - QR Code Button + User Profile */}
                <div className="nav-right">
                    {/* QR Code Button - Always visible */}
                    <button
                        className="nav-qr-code-btn"
                        onClick={handleQRCodeClick}
                        disabled={loadingQRCode}
                        aria-label="Show QR Code"
                        title="Event Ticket"
                    >
                        <QrCode size={20} />
                    </button>
                    
                    <div className="user-profile" onClick={toggleDropdown} ref={dropdownRef}>
                        <div className="user-avatar">
                            {userDetails?.image_url ? (
                                <img src={userDetails.image_url} alt={getDisplayName()} className="avatar-image" />
                            ) : (
                                getInitials()
                            )}
                        </div>
                        <span className="user-name">{getDisplayName()}</span>
                        <svg
                            className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}
                            width="12"
                            height="8"
                            viewBox="0 0 12 8"
                            fill="none"
                        >
                            <path
                                d="M1 1L6 6L11 1"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>

                        {/* Dropdown Menu */}
                        {isDropdownOpen && (
                            <div className="dropdown-menu">
                                <div className="dropdown-header">
                                    <div className="dropdown-user-info">
                                        <div className="dropdown-name">{userDetails?.full_name || getDisplayName()}</div>
                                        <div className="dropdown-email">{userDetails?.email}</div>
                                    </div>
                                </div>
                                <div className="dropdown-divider"></div>
                                <button 
                                    className="dropdown-item" 
                                    onClick={handleQRCodeClick}
                                    disabled={loadingQRCode}
                                >
                                    <QrCode size={16} />
                                    {loadingQRCode ? 'Loading...' : 'Event Ticket'}
                                </button>
                                <button className="dropdown-item" onClick={() => {
                                    setIsDropdownOpen(false);
                                    navigate(`/user/${userDetails?.contact_id}`);
                                }}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M8 8C10.21 8 12 6.21 12 4C12 1.79 10.21 0 8 0C5.79 0 4 1.79 4 4C4 6.21 5.79 8 8 8ZM8 10C5.33 10 0 11.34 0 14V16H16V14C16 11.34 10.67 10 8 10Z" fill="currentColor" />
                                    </svg>
                                    My Profile
                                </button>
                                {/* Settings button - commented out for now
                                <button className="dropdown-item" onClick={() => {
                                    setIsDropdownOpen(false);
                                    navigate('/settings');
                                }}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M13.4 7.2L12.8 6.4C12.72 6.24 12.72 6.04 12.8 5.88L13.32 5.08C13.48 4.84 13.44 4.52 13.24 4.32L11.68 2.76C11.48 2.56 11.16 2.52 10.92 2.68L10.12 3.2C9.96 3.28 9.76 3.28 9.6 3.2L8.8 2.6C8.6 2.48 8.48 2.28 8.48 2.04V0.8C8.48 0.36 8.12 0 7.68 0H5.28C4.84 0 4.48 0.36 4.48 0.8V2.04C4.48 2.28 4.36 2.48 4.16 2.6L3.36 3.2C3.2 3.28 3 3.28 2.84 3.2L2.04 2.68C1.8 2.52 1.48 2.56 1.28 2.76L-0.28 4.32C-0.48 4.52 -0.52 4.84 -0.36 5.08L0.16 5.88C0.24 6.04 0.24 6.24 0.16 6.4L-0.44 7.2C-0.56 7.4 -0.56 7.64 -0.44 7.84L0.16 8.64C0.24 8.8 0.24 9 0.16 9.16L-0.36 9.96C-0.52 10.2 -0.48 10.52 -0.28 10.72L1.28 12.28C1.48 12.48 1.8 12.52 2.04 12.36L2.84 11.84C3 11.76 3.2 11.76 3.36 11.84L4.16 12.44C4.36 12.56 4.48 12.76 4.48 13V14.24C4.48 14.68 4.84 15.04 5.28 15.04H7.68C8.12 15.04 8.48 14.68 8.48 14.24V13C8.48 12.76 8.6 12.56 8.8 12.44L9.6 11.84C9.76 11.76 9.96 11.76 10.12 11.84L10.92 12.36C11.16 12.52 11.48 12.48 11.68 12.28L13.24 10.72C13.44 10.52 13.48 10.2 13.32 9.96L12.8 9.16C12.72 9 12.72 8.8 12.8 8.64L13.4 7.84C13.52 7.64 13.52 7.4 13.4 7.2ZM6.48 9.52C5.08 9.52 3.96 8.4 3.96 7C3.96 5.6 5.08 4.48 6.48 4.48C7.88 4.48 9 5.6 9 7C9 8.4 7.88 9.52 6.48 9.52Z" fill="currentColor" />
                                    </svg>
                                    Settings
                                </button>
                                <div className="dropdown-divider"></div>
                                */}
                                <button className="dropdown-item logout" onClick={handleLogout}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M6 14H3C2.73478 14 2.48043 13.8946 2.29289 13.7071C2.10536 13.5196 2 13.2652 2 13V3C2 2.73478 2.10536 2.48043 2.29289 2.29289C2.48043 2.10536 2.73478 2 3 2H6M11 11L14 8M14 8L11 5M14 8H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    Logout
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </nav>
        </>
    );
};

export default NavigationBar;

